// Reusable search-selector (type-to-filter combobox) — BYTE-PARITY port of
// invoiceTest/src/searchselect.js (owner 2026-09-05: every dropdown in the transplanted screens
// must search and keyboard exactly like invoiceTest — "there cannot be dropdowns"). As the user
// types it filters the options and INFERS the likely match (auto-highlights the best hit;
// Enter / Tab / blur / click commits it). Rich fields (contact, line items) layer their own
// inference via onType/onSelect; small enums get instant local type-ahead here.
//
// Built imperatively (input is a stable node, only the popup list rebuilds) so it never trips the
// tina4-js "input inside a reactive block loses focus" footgun — and it's plain DOM
// (createElement), which also sidesteps the whole-attribute-interpolation footgun. The popup is a
// FIXED-position layer appended to <body>, so it is never clipped by a table's overflow-x:auto.
//
// Do NOT "improve" the keyboard behaviour here — every branch below encodes an owner-reviewed
// invoiceTest ruling (Tab-commit 2026-08-28, empty-Enter bubble UNLESS the user arrow-navigated
// onto an option 2026-09-09, free-text keep-typed 2026-08-07, sync blur commit 2026-08-07,
// launcher commitOnBlur:false, Escape onType re-sync). Change it in invoiceTest first, then mirror.

const norm = s => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// options: [{ value, label, sub?, keywords? }]  (value is coerced to string for matching)
// opts: { value, placeholder, allowFree, className, ariaLabel, onSelect(value,label,opt), onType(text),
//         onCreate(typedText), createLabel, commitOnBlur, emptyText }
//
// onCreate adds a "+ Create '<what you typed>'" row at the TOP of the popup whenever the typed text
// doesn't exactly match an existing option — the Xero pattern ("you must ALWAYS be able to easily
// add a customer when you create the screen"). The caller does the creating and then calls
// .setOptions(...) + .set(newValue).
export function searchSelect(opts = {}) {
  let options = normalizeOptions(opts.options || []);
  const allowFree = !!opts.allowFree;
  const onSelect = opts.onSelect || (() => {});
  const onType = opts.onType || null;
  const onCreate = opts.onCreate || null;
  // A LAUNCHER (global search) must not commit-on-blur — snapping to the first match and firing
  // onSelect there navigates the user somewhere they never chose.
  const commitOnBlur = opts.commitOnBlur !== false;
  const createLabel = opts.createLabel || "+ Create ‘{q}’";
  let emptyText = opts.emptyText || 'No matches';

  let current = opts.value == null ? '' : String(opts.value);
  let open = false;
  let activeIdx = -1;
  let filtered = options.slice();
  let listEl = null;

  const root = document.createElement('div');
  root.className = 'ss';
  const input = document.createElement('input');
  input.className = 'ss-input';
  if (opts.className) opts.className.split(/\s+/).forEach(c => c && input.classList.add(c));
  input.type = 'text';
  input.autocomplete = 'off';
  input.spellcheck = false;
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-expanded', 'false');
  input.placeholder = opts.placeholder || 'Type to search…';
  if (opts.ariaLabel) input.setAttribute('aria-label', opts.ariaLabel);
  root.append(input);
  // Visible dropdown affordance (owner 2026-09-14: "dropdowns need a visible chevron"). A real,
  // non-focusable node (not a ::after) so it's testable and never depends on jsdom pseudo-element
  // support; pointer-events:none in CSS so a click still lands on the input and opens the popup.
  const caret = document.createElement('span');
  caret.className = 'ss-caret';
  caret.setAttribute('aria-hidden', 'true');
  caret.textContent = '▾';
  root.append(caret);
  input.value = labelFor(current);

  function normalizeOptions(list) {
    return list.map(o => {
      const value = String(o.value);
      const label = o.label == null ? value : String(o.label);
      return { value, label, sub: o.sub || '', _n: norm(label + ' ' + (o.keywords || '')) };
    });
  }
  function labelFor(value) {
    const o = options.find(x => x.value === String(value));
    return o ? o.label : (allowFree ? String(value) : '');
  }
  function computeFiltered(query) {
    const nq = norm(query);
    if (!nq) return options.slice();
    const starts = [], contains = [];
    for (const o of options) {
      if (o._n.startsWith(nq)) starts.push(o);
      else if (o._n.includes(nq)) contains.push(o);
    }
    return [...starts, ...contains];
  }

  function ensureList() {
    if (listEl) return;
    listEl = document.createElement('ul');
    listEl.className = 'ss-list';
    listEl.setAttribute('role', 'listbox');
    // mousedown (not click) so the option is chosen BEFORE the input blur fires
    listEl.addEventListener('mousedown', e => {
      const li = e.target.closest('.ss-opt');
      if (!li || li.classList.contains('is-empty')) return;
      e.preventDefault();
      if (li.classList.contains('ss-create')) { const q = li.dataset.typed || input.value.trim(); closeList(); onCreate(q); return; }
      choose(filtered[Number(li.dataset.i)]);
    });
  }
  // The "+ Create ‘X’" row — shown only when a create handler exists, something was typed, and it
  // doesn't already exactly match an option (so you can't create a duplicate of what you just found).
  function createRow() {
    if (!onCreate) return null;
    const typed = input.value.trim();
    if (!typed) return null;
    if (options.some(o => norm(o.label) === norm(typed))) return null;
    const li = document.createElement('li');
    li.className = 'ss-opt ss-create';
    li.setAttribute('role', 'option');
    li.dataset.typed = typed;                 // captured here so the click uses it even if input clears
    li.textContent = createLabel.replace('{q}', typed);
    return li;
  }
  function renderList() {
    ensureList();
    listEl.replaceChildren();
    const mk = createRow();
    if (mk) listEl.append(mk);
    if (!filtered.length) {
      // Nothing matched. Keep the popup open when there's a create row to offer, otherwise fall
      // back: free-text fields just close; fixed lists show "No matches".
      if (!mk && allowFree) return closeList();
      if (!mk) {
        const li = document.createElement('li');
        li.className = 'ss-opt is-empty';
        li.textContent = emptyText;
        listEl.append(li);
      }
    } else {
      filtered.forEach((o, i) => {
        const li = document.createElement('li');
        li.className = 'ss-opt' + (i === activeIdx ? ' active' : '') + (o.value === current ? ' sel' : '');
        li.setAttribute('role', 'option');
        li.dataset.i = String(i);
        const lab = document.createElement('span');
        lab.className = 'ss-lab';
        lab.textContent = o.label;
        li.append(lab);
        if (o.sub) { const s = document.createElement('span'); s.className = 'ss-sub'; s.textContent = o.sub; li.append(s); }
        listEl.append(li);
      });
    }
    if (!listEl.isConnected) document.body.append(listEl);
    position();
    const act = listEl.querySelector('.ss-opt.active');
    if (act && act.scrollIntoView) act.scrollIntoView({ block: 'nearest' });
  }
  function position() {
    if (!listEl) return;
    if (!input.isConnected) { closeList(); return; }   // input torn down (route/re-render) — don't orphan the popup + its scroll/resize listeners
    const r = input.getBoundingClientRect();
    // The popup is at least as wide as the input, never below a floor. A narrow-column picker (e.g.
    // the ~92px VAT cell) passes a larger minWidth so long option names (Finnish tax codes) aren't
    // clipped; it is still clamped to the viewport below.
    const width = Math.max(r.width, opts.minWidth || 180);
    let left = Math.min(r.left, window.innerWidth - width - 8);
    left = Math.max(8, left);
    listEl.style.width = width + 'px';
    listEl.style.left = left + 'px';
    listEl.style.top = (r.bottom + 2) + 'px';           // measure with a provisional top
    const h = listEl.offsetHeight;
    if (r.bottom + 2 + h > window.innerHeight - 8 && r.top - 2 - h > 8) listEl.style.top = (r.top - 2 - h) + 'px';
  }
  const onScroll = () => { if (open) position(); };

  function openList() {
    open = true;
    input.setAttribute('aria-expanded', 'true');
    renderList();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
  }
  function closeList() {
    open = false;
    activeIdx = -1;
    input.setAttribute('aria-expanded', 'false');
    if (listEl && listEl.isConnected) listEl.remove();
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', onScroll);
  }
  function choose(o) {
    if (!o) return;
    const changed = o.value !== current;
    current = o.value;
    input.value = o.label;
    closeList();
    if (changed) onSelect(current, o.label, o);
  }
  function commit() {
    const typed = input.value;
    // An empty/whitespace box must never match an option: norm('') === norm('%'), so clearing a
    // 2-option MODE box used to snap to "%" and turn a R500 discount into 500%. No query → revert
    // to the current selection (or clear a free field).
    if (!norm(typed)) {
      if (allowFree && typed !== labelFor(current)) { current = typed; onSelect(typed, typed, null); }
      else input.value = labelFor(current);
      return;
    }
    // The box still shows the current option's own label: nothing new was typed, so keep it. Options can
    // share a label (every SA 15% VAT code reads "15%"), and matching by label below would re-pick the
    // FIRST of them — leaving the box turned "15% Input Tax" into "15% Output Tax" (2026-10-09).
    if (typed === labelFor(current)) return;
    const exact = options.find(o => norm(o.label) === norm(typed));
    if (exact) { choose(exact); return; }
    if (allowFree) {
      if (typed !== labelFor(current)) { current = typed; onSelect(typed, typed, null); }
      return;
    }
    const f = computeFiltered(typed);
    if (f.length) choose(f[0]);                          // snap to the inferred best match
    else input.value = labelFor(current);                // nothing usable typed → revert
  }

  input.addEventListener('focus', () => {
    filtered = computeFiltered('');
    activeIdx = Math.max(0, filtered.findIndex(o => o.value === current));
    // Select the current text so the user can just type to replace it (owner Andre 2026-09-18:
    // "Must select all text so I can type / replace").
    try { input.select(); } catch { /* not selectable */ }
    openList();
  });
  let arrowNavigated = false;                            // did the user ACTIVELY move the highlight?
  input.addEventListener('input', () => {
    const text = input.value;
    if (onType) onType(text);
    filtered = computeFiltered(text);
    activeIdx = filtered.length ? 0 : -1;                // infer: best match is pre-highlighted
    arrowNavigated = false;                              // fresh typing resets deliberate navigation
    if (!open) openList(); else renderList();
  });
  input.addEventListener('keydown', e => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) openList(); activeIdx = Math.min(activeIdx + 1, filtered.length - 1); arrowNavigated = true; renderList(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); if (open) { activeIdx = Math.max(activeIdx - 1, 0); arrowNavigated = true; renderList(); } }
    else if (e.key === 'Enter') {
      const typed = input.value.trim();
      // Empty field: commit the highlight ONLY when the user DELIBERATELY arrowed onto it
      // (arrowNavigated) — that's "use the arrows to navigate, press Enter to select" with nothing
      // typed. A plain Enter on an untouched empty box still bubbles (activeIdx defaults to 0 on
      // focus, which is NOT a deliberate pick) so a line-grid's Enter-chain can advance past it.
      if (!typed) {
        if (open && arrowNavigated && activeIdx >= 0 && filtered[activeIdx]) { e.preventDefault(); choose(filtered[activeIdx]); }
        return;
      }
      const active = open && activeIdx >= 0 ? filtered[activeIdx] : null;
      // FREE-TEXT field: take the highlighted option ONLY when the user deliberately arrowed onto
      // it; otherwise keep exactly what they typed (a custom "annual" must not snap to "Website
      // hosting — annual"). Fixed-list fields still snap.
      if (active && (!allowFree || arrowNavigated)) { e.preventDefault(); choose(active); }
      else if (allowFree) { e.preventDefault(); commit(); closeList(); }
      // Nothing to pick but a create row is showing → Enter creates (keyboard parity with clicking it).
      else if (open && onCreate && !filtered.length) { e.preventDefault(); closeList(); onCreate(typed); }
    }
    // Tab commits the highlighted option too — handled on keydown, NOT via blur, because it must
    // fire reliably; we do NOT preventDefault, so Tab still moves focus to the next field. Mirrors
    // Enter: only takes the highlight when the user arrowed onto it (free-text fields keep a typed
    // value), so a plain Tab-through still commits typed text on blur.
    else if (e.key === 'Tab') {
      const active = open && activeIdx >= 0 ? filtered[activeIdx] : null;
      if (active && (!allowFree || arrowNavigated)) choose(active);
    }
    else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault(); input.value = labelFor(current); closeList();
        // The model heard every keystroke via onType — tell it about the revert too, or the
        // visible field and the saved value silently disagree.
        if (allowFree && onType) onType(input.value);
      }
    }
  });
  input.addEventListener('blur', () => {
    // Commit SYNCHRONOUSLY: option/create clicks preventDefault on mousedown so focus never leaves
    // and this never fires for them — but a click on a real button (Approve, Save) blurs the
    // input, and a deferred (setTimeout) commit would run only AFTER that button's click handler
    // had already read a stale model. A launcher opts out of committing entirely
    // (commitOnBlur:false).
    // Tab parity with Enter: if the user arrow-navigated to an option, blurring (i.e. Tab) commits
    // THAT highlight — otherwise fall back to committing the typed text, so a custom free-text
    // value the user typed (never arrowed) is still kept.
    if (commitOnBlur && open && arrowNavigated && activeIdx >= 0 && filtered[activeIdx]) { choose(filtered[activeIdx]); return; }
    if (commitOnBlur) commit();
    else if (current) input.value = labelFor(current);   // non-committing box: drop stale typed text, restore the real selection
    closeList();
  });

  return {
    el: root,
    input,
    get: () => current,
    set(value) { current = value == null ? '' : String(value); input.value = labelFor(current); },
    setOptions(list) { options = normalizeOptions(list); if (open) { filtered = computeFiltered(input.value); renderList(); } },
    // The message shown when the list is empty — callers change it to reflect WHY it's empty
    // (e.g. "Pick a country first" vs "No entity types for this country").
    setEmptyText(text) { emptyText = text || 'No matches'; if (open) renderList(); },
  };
}
